import axios, { AxiosError } from 'axios';
import { stringify } from 'query-string';
import invariant from 'tiny-invariant';

import { clearAuthenticationState } from '../auth/clearAuthState';
import { backendHost } from '../utils/axleUrls';
import { envVars } from '../utils/envVars';

const axiosClient = axios.create({
    // This 1. tells the browser to include credentials with XMLHttpRequests to different domains,
    //  and 2. allows the HTTP response to set cookies for its own domain.
    // In our case, the different domain is api.axlehealth.com, and we want to send for the
    //  session ID cookies and CSRF cookies to be sent along with HTTP requests and for
    //  the HTTP responses to set the cookie values.
    //
    // See:
    // - https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials
    // - https://stackoverflow.com/questions/43002444/make-axios-send-cookies-in-its-requests-automatically#comment107961858_54470754
    //
    withCredentials: true,
    withXSRFToken: true,
    // This tells axios to read the CSRF token from the configured cookie name (environment-specific,
    //  e.g., 'csrftoken' or 'csrftoken_demo') and send its value in the header 'X-CSRFTOKEN'.
    //  Django is configured by default to check for the CSRF token not just in the form POST data
    //  but in the request headers too. The cookie name can be customized per environment via
    //  REACT_APP_CSRF_COOKIE_NAME to prevent cookie collisions between environments.
    // This needs `axios.defaults.withCredentials = true` to work.
    //
    // See:
    // - https://alone-djangonaut.com/django-axios-and-csrf-token
    //
    xsrfHeaderName: 'X-CSRFTOKEN',
    xsrfCookieName: envVars.csrfCookieName,
    // Make Axios encode array values in GET requests as `arr=value1&arr=value2` instead of
    //  `arr[]=value1&arr[]=value2` (which is the default Axios behaviour).
    // This way, on the backend, we can do `request.query_params.getlist('arr')` to get the
    //  list of values. That doesn’t work with the `arr[]=value1&arr[]=value2` format.
    //  See: https://stackoverflow.com/a/74967210
    // Note additionally that `{a: 'a', b: null, c: undefined}` becomes `?a=a&b`, i.e., null
    //  means no value, and undefined means the key is not present.
    paramsSerializer: (params: Record<string, unknown>) => stringify(params),
});

export const backendEndpointsPrefix = `${backendHost}partner/v1`;

// Intercept requests and:
// 1. set a URL prefix.
// 2. set the HTTP "Accept" header.
axiosClient.interceptors.request.use(
    (requestConfig) => {
        invariant(requestConfig.url, 'requestConfig.url is not set');
        requestConfig.url = `${backendEndpointsPrefix}${requestConfig.url}`;
        requestConfig.headers.Accept = 'application/json';
        // Stamp the start time so, if the request fails, we can record how long it ran
        //  before failing (see extractAxiosErrorContext). Useful for diagnosing timeouts
        //  and backend slowdowns. Uses performance.now() for a monotonic duration.
        requestConfig.metadata = { startTime: performance.now() };
        return requestConfig;
    },
    (error) => Promise.reject(error),
);

// If any API request returns a 401 "Unauthenticated" error, redirect the user
//  to the sign-in page to reauthenticate them.
// This is important for when sessions expire, and we want the users to have a
//  smooth experience being redirected to the login and redirected back to the page
//  they were on.
const intercept401 = (error: AxiosError) => {
    if (error.response?.status === 401) {
        // By setting isAuthenticated to false, this function will automatically
        //  cause the router to redirect the user to the sign-in page if they
        //  were on a page requiring authentication.
        clearAuthenticationState();
        return new Promise(() => undefined); // Return a promise that will never resolve
    } else {
        return Promise.reject(error);
    }
};

axiosClient.interceptors.response.use(undefined, intercept401);

export const useAxiosClient = () => axiosClient;
